import { type CSSProperties, type KeyboardEvent } from "react";
import { useTime, VaultGlyph, AlarmBell } from "./core.js";

/**
 * Tile + TileGrid — the 5×5 vault floor for Pressure Plate.
 * State machine per cell:
 *   hidden | hover | revealed-safe | revealed-mine | bust-other
 * Bust-other is what every untouched tile becomes when the round resolves;
 * mines are revealed at low opacity so the player sees what they avoided.
 */

export type TileState =
  | "hidden"
  | "hover"
  | "revealed-safe"
  | "revealed-mine"
  | "bust-other";

const GRID_LETTERS = ["A", "B", "C", "D", "E"];

export interface TileProps {
  row: number;
  col: number;
  state?: TileState;
  flashAge?: number;
  mineUnderneath?: boolean;
  glyphScale?: number;
  size?: number;
  onClick?: () => void;
}

export function Tile({
  row,
  col,
  state = "hidden",
  flashAge = 0,
  mineUnderneath = false,
  glyphScale = 1,
  size = 96,
  onClick,
}: TileProps) {
  const t = useTime();
  const isHidden = state === "hidden";
  const isHover = state === "hover";
  const isRevealedSafe = state === "revealed-safe";
  const isRevealedMine = state === "revealed-mine";
  const isBustOther = state === "bust-other";

  // Soft pulse on idle hidden tiles — communicates "alive, awaiting input."
  const breathe = isHidden ? 0.7 + 0.3 * Math.sin(t * 1.2 + (row + col) * 0.4) : 1;
  // Recently-revealed tiles glow brighter, decaying.
  const fresh =
    isRevealedSafe || isRevealedMine ? Math.max(0, 1 - flashAge * 0.6) : 0;

  let bg: string;
  let fg: string;
  let edgeColor: string;
  let glow: string;
  if (isHidden) {
    bg = "linear-gradient(180deg, #161B27 0%, #0F1320 100%)";
    fg = "var(--fg-3)";
    edgeColor = "var(--line)";
    glow = "none";
  } else if (isHover) {
    bg = "linear-gradient(180deg, #1F2536 0%, #161B27 100%)";
    fg = "var(--gold-bright)";
    edgeColor = "var(--line-strong)";
    glow = "0 0 0 1px rgba(241,203,107,0.3), 0 0 18px rgba(241,203,107,0.18)";
  } else if (isRevealedSafe) {
    bg = `linear-gradient(180deg, rgba(196,154,70,${0.18 + fresh * 0.2}) 0%, rgba(140,106,42,${0.10 + fresh * 0.15}) 100%), linear-gradient(180deg, #161B27 0%, #0F1320 100%)`;
    fg = "var(--gold-bright)";
    edgeColor = "var(--line-strong)";
    glow = `inset 0 0 ${24 + fresh * 30}px rgba(241,203,107,${0.18 + fresh * 0.45}), 0 0 ${12 + fresh * 22}px rgba(241,203,107,${0.18 + fresh * 0.4})`;
  } else if (isRevealedMine) {
    bg = `linear-gradient(180deg, rgba(224,69,69,${0.32 + fresh * 0.4}) 0%, rgba(106,26,26,${0.32 + fresh * 0.4}) 100%), linear-gradient(180deg, #161B27 0%, #0F1320 100%)`;
    fg = "var(--red-bright)";
    edgeColor = "rgba(255,106,106,0.7)";
    glow = `inset 0 0 ${30 + fresh * 30}px rgba(224,69,69,${0.4 + fresh * 0.4}), 0 0 ${20 + fresh * 30}px rgba(224,69,69,${0.4 + fresh * 0.5})`;
  } else {
    bg = mineUnderneath
      ? "linear-gradient(180deg, rgba(106,26,26,0.16) 0%, rgba(60,16,16,0.16) 100%), #0B0E16"
      : "linear-gradient(180deg, #0E1220 0%, #080B14 100%)";
    fg = mineUnderneath ? "rgba(224,69,69,0.55)" : "var(--fg-4)";
    edgeColor = mineUnderneath ? "rgba(224,69,69,0.25)" : "rgba(196,154,70,0.06)";
    glow = "none";
  }

  const aria = `Tile ${GRID_LETTERS[col]}${row + 1}, ${
    isHidden
      ? "untouched"
      : isRevealedSafe
        ? "safe, vault-glyph revealed"
        : isRevealedMine
          ? "mine triggered, alarm tripped"
          : isBustOther && mineUnderneath
            ? "mine, not triggered"
            : "untouched"
  }`;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!isHidden || !onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const tileStyle: CSSProperties = {
    position: "relative",
    width: size,
    height: size,
    background: bg,
    border: `1px solid ${edgeColor}`,
    borderRadius: 2,
    boxShadow: glow,
    cursor: isHidden && onClick ? "pointer" : "default",
    opacity: isBustOther ? 0.7 : breathe,
    transition:
      "background 200ms var(--ease-snap), box-shadow 200ms var(--ease-snap), opacity 280ms var(--ease-fade)",
    display: "grid",
    placeItems: "center",
    color: fg,
    overflow: "hidden",
  };

  return (
    <div
      role="button"
      aria-label={aria}
      tabIndex={isHidden && onClick ? 0 : -1}
      onClick={isHidden && onClick ? onClick : undefined}
      onKeyDown={handleKey}
      // Used by the keyboard-nav handler in PressurePlate.tsx to find the
      // next focus target via querySelector. Cheap, no API surface.
      data-pp-row={row}
      data-pp-col={col}
      style={tileStyle}
    >
      {/* coordinate label — top-left, always present, dim once revealed */}
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          color: "var(--fg-4)",
          letterSpacing: 1,
          fontWeight: 500,
          opacity: isHidden || isHover ? 1 : 0.4,
        }}
      >
        {GRID_LETTERS[col]}
        {row + 1}
      </div>

      {isRevealedSafe && (
        <div
          style={{
            transform: `scale(${1 + fresh * 0.3})`,
            transition: "transform 180ms var(--ease-snap)",
            filter: `drop-shadow(0 0 ${4 + fresh * 8}px var(--gold-bright))`,
          }}
        >
          <VaultGlyph size={Math.round(size * 0.34 * glyphScale)} color="var(--gold-bright)" />
        </div>
      )}
      {isRevealedMine && (
        <div
          style={{
            transform: `scale(${1 + fresh * 0.5})`,
            filter: `drop-shadow(0 0 ${6 + fresh * 10}px var(--red-bright))`,
          }}
        >
          <AlarmBell size={Math.round(size * 0.36 * glyphScale)} color="var(--red-bright)" />
        </div>
      )}
      {isBustOther && mineUnderneath && (
        <div style={{ opacity: 0.55 }}>
          <AlarmBell size={Math.round(size * 0.28 * glyphScale)} color="rgba(224,69,69,0.7)" />
        </div>
      )}
      {isHover && (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: Math.round(size * 0.13),
            color: "var(--gold-bright)",
            letterSpacing: 1,
            fontWeight: 500,
          }}
        >
          ·
        </div>
      )}

      {/* shockwave on revealed-mine */}
      {isRevealedMine && fresh > 0.05 && (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              border: `2px solid var(--red-bright)`,
              borderRadius: 2,
              opacity: fresh * 0.7,
              transform: `scale(${1 + (1 - fresh) * 0.4})`,
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              border: `1px solid var(--red)`,
              borderRadius: 2,
              opacity: fresh * 0.4,
              transform: `scale(${1 + (1 - fresh) * 0.7})`,
            }}
          />
        </>
      )}
    </div>
  );
}

export interface TileGridProps {
  state: TileState[][];
  mines?: boolean[][];
  size?: number;
  gap?: number;
  glyphScale?: number;
  onTileClick?: (row: number, col: number) => void;
  flashAges?: number[][];
}

export function TileGrid({
  state,
  mines,
  size = 96,
  gap = 10,
  glyphScale = 1,
  onTileClick,
  flashAges,
}: TileGridProps) {
  const rows = state.length;
  const cols = state[0]?.length ?? 5;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${size}px)`,
        gridTemplateRows: `repeat(${rows}, ${size}px)`,
        gap,
        padding: gap,
        background: `
          linear-gradient(180deg, rgba(196,154,70,0.04) 0%, transparent 30%),
          radial-gradient(ellipse 80% 50% at 50% 0%, rgba(241,203,107,0.05) 0%, transparent 60%),
          #0A0D14
        `,
        border: "1px solid var(--line-strong)",
        borderRadius: 4,
        boxShadow: "var(--shadow-deep), inset 0 1px 0 rgba(255,255,255,0.03)",
        position: "relative",
      }}
    >
      {state.flatMap((row, r) =>
        row.map((s, c) => {
          const flashAge = flashAges?.[r]?.[c] ?? 0;
          return (
            <Tile
              key={`${r}-${c}`}
              row={r}
              col={c}
              state={s}
              mineUnderneath={mines?.[r]?.[c] ?? false}
              size={size}
              glyphScale={glyphScale}
              flashAge={flashAge}
              onClick={onTileClick ? () => onTileClick(r, c) : undefined}
            />
          );
        }),
      )}

      {/* Floor seam crosshatch — almost imperceptible, brand alignment */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `linear-gradient(0deg, var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
          opacity: 0.18,
          mixBlendMode: "overlay",
        }}
      />
    </div>
  );
}
